import React from 'react';
import classnames from 'classnames';
import snakeCase from 'lodash/snakeCase';
import { useMediaQuery } from 'react-responsive';
import { Button, Card, Typography } from '@guildeducationinc/recess';
import { useEmployerContent } from '@src/context/EmployerContentContext';
import { ComponentDataKey } from '@src/components/Pages/Home/Layout/config';
import { clickable } from '@src/analytics';
import { useModal } from '@src/context/ModalContext';
import { STUDENT_LOAN_CARD_QUERY } from '@src/constants';
import Skeleton from '@src/components/Shared/Skeleton/Skeleton';
import StudentLoanModal from '../StudentLoanModal/StudentLoanModal';
import './StudentLoanCard.scss';

const StudentLoanCard: React.FC = () => {
  const isMobile = useMediaQuery(STUDENT_LOAN_CARD_QUERY);
  const dataKey = ComponentDataKey.STUDENT_LOAN;
  const { [dataKey]: content } = useEmployerContent() ?? {};
  const { openModal } = useModal();
  const hasCardBody = !!content?.card.body;

  const cardClassNames = classnames('studentLoanCard', 'boxShadow--sm', 'padding--y_12');

  const handleOpenModal = () => {
    showModalClickable();
    openModal(<StudentLoanModal />);
  };

  const showModalClickable = () => {
    clickable({
      loggedIn: true,
      product: 'home',
      tag: 'sidebar_student_loan_card',
      text: snakeCase(content?.card.modalButtonCopy),
      type: 'button',
    });
  };

  const ssoClickable = () => {
    clickable({
      loggedIn: true,
      product: 'home',
      tag: 'sidebar_student_loan_card',
      text: snakeCase(content?.card.ssoLinkCopy),
      type: 'link',
    });
  };

  return (
    <Card className={cardClassNames} testid='studentLoanCard'>
      <Card.Content>
        <Skeleton>
          <Typography variant='h2'>{content?.card.title}</Typography>
        </Skeleton>
      </Card.Content>
      {hasCardBody && (
        <>
          <Card.Content style={{ marginTop: 16 }}>
            <Skeleton>
              <Typography variant='title3'>{content?.card.subtitle}</Typography>
            </Skeleton>
            <Skeleton count={3}>
              <Typography
                variant='body1'
                className='studentLoanCardBody padding--top_16 padding--bottom_24'
              >
                {content?.card.body}
              </Typography>
              <Button
                className={isMobile ? 'margin--bottom_8' : 'margin--right_16'}
                appearance='outline'
                fullWidth={isMobile}
                label='Learn more about paying off your student loans.'
                onClick={handleOpenModal}
              >
                {content?.card.modalButtonCopy}
              </Button>
              <Button
                appearance='ghost'
                fullWidth={isMobile}
                href={content?.card.ssoLink}
                newWindowIcon={true}
                newWindowNotification={true}
                onClick={ssoClickable}
                target='_blank'
              >
                {content?.card.ssoLinkCopy}
              </Button>
            </Skeleton>
          </Card.Content>
        </>
      )}
    </Card>
  );
};

export default StudentLoanCard;
